/* eslint-disable no-underscore-dangle */
/* eslint-disable import/no-extraneous-dependencies */
/* eslint-disable no-return-await */
/* eslint-disable react/jsx-props-no-spreading */
// eslint-disable-next-line no-redeclare, @typescript-eslint/no-redeclare
import { Box, Group, Text, ThemeIcon } from '@mantine/core';
import { Content, Editor, EditorProvider, Extensions } from '@tiptap/react';
import { CSSProperties, FC, ReactNode, useEffect, useState } from 'react';
import { i18n } from '@lingui/core';
import Paragraph from '@tiptap/extension-paragraph';
import { useId } from '@mantine/hooks';
import { Editor as RteEditor } from '@tiptap/core';
import {
  replacePlaceholdersHtmlWithPlaceholdersId,
  replacePlaceholdersWithHtmlSettings,
} from '../../Service/Placeholder/PlaceholderResover';
import { TiptapS } from '../../Service/TiptapS';
import TooltipButton from '../../Atoms/buttons/TooltipButton';
import useRteStyles from '../../Service/hooks/useRteStyles';
import { EventPlaceholderExtension, SettingsPlaceholderExtension } from './placeholder/PlaceholderExtensions';
import { IconHash } from '@tabler/icons';

interface Props {
  value: string;
  label?: string;
  description?: string;
  small?: boolean;
  patchField?: (value: string) => Promise<boolean>;
  onUpdate?: (html: string) => void;
  toolbar?: ReactNode;
  menu?: ReactNode;
  withUpdate?: boolean;
  extendLineHeight?: boolean;
  setEditor?: (editor: Editor) => unknown;
  height?: number;
  textAlign?: string;
  styles?: CSSProperties;
}

export const EventBaseInput: FC<Props> = ({ value, patchField, onUpdate, ...rest }) => {
  const content = replacePlaceholdersWithHtmlSettings(i18n, value);
  const preChange = (html: string) => TiptapS.removeParagraphWrapper(replacePlaceholdersHtmlWithPlaceholdersId(html));

  const onChange = async (html: string) => {
    if (patchField) {
      return patchField(preChange(html));
    }
    return Promise.resolve(true);
  };

  const _onUpdate = (html: string) => {
    if (onUpdate) {
      onUpdate(preChange(html));
    }
  };

  return (
    <TiptapInput
      extensions={[...TiptapS.Extentions.MINIMAL, EventPlaceholderExtension]}
      content={content}
      onChange={patchField ? onChange : undefined}
      onUpdate={onUpdate ? _onUpdate : undefined}
      {...rest}
    />
  );
};

export const BaseInput: FC<Props> = ({ value, patchField, ...rest }) => {
  const content = replacePlaceholdersWithHtmlSettings(i18n, value);
  const preChange = (html: string) => TiptapS.removeParagraphWrapper(replacePlaceholdersHtmlWithPlaceholdersId(html));

  const onChange = async (html: string) => {
    if (patchField) {
      return patchField(preChange(html));
    }
    return Promise.resolve(true);
  };

  return (
    <TiptapInput
      extensions={[...TiptapS.Extentions.MINIMAL, SettingsPlaceholderExtension]}
      content={content}
      onChange={patchField ? onChange : undefined}
      {...rest}
    />
  );
};

export const RichInputWithoutMentions: FC<Props> = ({ value, patchField, ...rest }) => (
  <TiptapInput extensions={TiptapS.Extentions.EXTENDED} content={value} onChange={patchField} {...rest} />
);

export const EventRichInput: FC<Props> = ({ value, patchField, onUpdate, ...rest }) => {
  const content = replacePlaceholdersWithHtmlSettings(i18n, value);
  const preChange = (html: string) => replacePlaceholdersHtmlWithPlaceholdersId(html);

  const onChange = async (html: string) => {
    if (patchField) {
      return patchField(preChange(html));
    }
    return Promise.resolve(true);
  };

  const _onUpdate = (html: string) => {
    if (onUpdate) {
      onUpdate(preChange(html));
    }
  };

  return (
    <TiptapInput
      extensions={[...TiptapS.Extentions.EXTENDED, EventPlaceholderExtension]}
      content={content}
      onChange={onChange}
      onUpdate={onUpdate ? _onUpdate : undefined}
      {...rest}
    />
  );
};

export const RichInput: FC<Props> = ({ ...rest }) => {
  const { value, patchField } = rest;
  const content = replacePlaceholdersWithHtmlSettings(i18n, value);

  const onChange = async (html: string) => {
    if (patchField) {
      const val = replacePlaceholdersHtmlWithPlaceholdersId(html);
      return patchField(val);
    }
    return Promise.resolve(true);
  };

  return (
    <TiptapInput
      extensions={[...TiptapS.Extentions.EXTENDED, SettingsPlaceholderExtension]}
      content={content}
      onChange={onChange}
      {...rest}
    />
  );
};

interface TiptapProps {
  extensions: Extensions;
  content: Content;
  onChange?: (html: string) => Promise<boolean>;
  onUpdate?: (html: string) => void;
  toolbar?: ReactNode;
  menu?: ReactNode;
  height?: number;
  label?: string;
  description?: string;
  extendLineHeight?: boolean;
  withUpdate?: boolean;
  setEditor?: (editor: Editor) => unknown;
  textAlign?: string;
  styles?: CSSProperties;
}

const TiptapInput: FC<TiptapProps> = ({
  extensions,
  content,
  height,
  onChange,
  onUpdate,
  label,
  description,
  toolbar,
  menu,
  textAlign,
  withUpdate = false,
  extendLineHeight = false,
  setEditor,
  styles,
}) => {
  const id = useId();
  const [focused, setFocused] = useState(false);
  const [showError, setShowError] = useState(false);
  const [rte, setRte] = useState<RteEditor>();
  const { classes, cx } = useRteStyles({ height, textAlign, styles, focused }, { name: 'TiptapEditor' });

  // Extend "line-height" only for an emails
  if (extendLineHeight) {
    extensions.push(
      Paragraph.configure({
        HTMLAttributes: {
          style: 'margin-bottom: 1.5em;',
        },
      }),
    );
  }

  useEffect(() => {
    if (withUpdate && rte && content && rte.getHTML() !== content) {
      rte.commands.setContent(content);
    }
  }, [rte, content, withUpdate]);

  // eslint-disable-next-line no-underscore-dangle
  const _onChange = async (value: string) => {
    if (onChange) {
      const success = await onChange(value);
      if (!success && !showError) {
        setShowError(true);
      }
      if (success && showError) {
        setShowError(false);
      }
    }
  };

  return (
    <>
      {label && (
        <Group spacing={5}>
          <Text mb={3} size={14} color={focused ? 'blue' : '#6E6E6E'} weight={focused ? 'bold' : 'normal'}>
            {label}
          </Text>
          {description && (
            <TooltipButton style={{ marginBottom: 3 }} icon="QUESTION" id={id} text={i18n._(description)} />
          )}
        </Group>
      )}
      <Box className={cx(classes.root)}>
        <EditorProvider
          injectCSS={false}
          extensions={extensions}
          content={content}
          slotAfter={toolbar}
          onFocus={() => setFocused(true)}
          onBlur={({ editor }) => {
            setFocused(false);
            if (onChange) {
              _onChange(editor.getHTML());
            }
          }}
          onUpdate={({ editor }) => {
            if (onUpdate) {
              onUpdate(editor.getHTML());
            }
          }}
          onCreate={({ editor }) => {
            setRte(editor);
            if (setEditor) {
              setEditor(editor as Editor);
            }
          }}
        >
          {menu}
        </EditorProvider>
      </Box>
      {showError && (
        <Text color="red" size="xs">
          {i18n._('error.input-field')}
        </Text>
      )}
    </>
  );
};
